import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const KEEP_SIGNED_IN_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours when "keep me signed in"
const WARNING_BEFORE_MS = 2 * 60 * 1000; // Warn 2 minutes before

// FIX #55: pages where the session timeout must NOT fire (password recovery flow)
const EXCLUDED_PATHS = ["/reset-password", "/verify-email"];

/**
 * Auto-logout after inactivity.
 * Tracks mouse, keyboard, touch, scroll events.
 * Shows a warning toast before logging out.
 * FIX #55: disabled on /reset-password so users mid-reset are not logged out.
 * Story #11: respects "keep me signed in" preference from localStorage.
 */
export function useSessionTimeout(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const { session, signOut } = useAuth();
  const location = useLocation();

  // Story #11: check if user opted for longer session
  const effectiveTimeout = (() => {
    try {
      if (localStorage.getItem("cc_keep_signed_in") === "true") return KEEP_SIGNED_IN_TIMEOUT_MS;
    } catch { /* storage unavailable — fall back to the default timeout */ }
    return timeoutMs;
  })();
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const warningToastIdRef = useRef<string | number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warningShownRef.current) {
      // FIX #27: dismiss the warning toast when user shows activity
      if (warningToastIdRef.current != null) {
        toast.dismiss(warningToastIdRef.current);
        warningToastIdRef.current = null;
      }
      warningShownRef.current = false;
    }
  }, []);

  useEffect(() => {
    // FIX #55: do not start the timer on excluded pages (password reset flow)
    if (!session) return;
    if (EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p))) return;
    // FIX #7: throttle mousemove to fire at most once per second to avoid hundreds of calls/sec on high-DPI
    let lastMoveTs = 0;
    const throttledMoveHandler = () => {
      const now = Date.now();
      if (now - lastMoveTs > 1000) {
        lastMoveTs = now;
        resetActivity();
      }
    };

    const immediateHandler = () => resetActivity();

    const immediateEvents = ["mousedown", "keydown", "touchstart", "scroll"];
    immediateEvents.forEach((e) => window.addEventListener(e, immediateHandler, { passive: true }));
    window.addEventListener("mousemove", throttledMoveHandler, { passive: true });

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = effectiveTimeout - elapsed;

      if (remaining <= 0) {
        // Auto-logout
        signOut();
        toast.info("You've been signed out due to inactivity.");
      } else if (remaining <= WARNING_BEFORE_MS && !warningShownRef.current) {
        warningShownRef.current = true;
        // FIX #27: store toast ID so we can dismiss it when user becomes active again
        warningToastIdRef.current = toast.warning("Session expiring soon — move your mouse to stay signed in.", {
          duration: 10000,
        });
      }
    }, 15000); // Check every 15 seconds

    return () => {
      immediateEvents.forEach((e) => window.removeEventListener(e, immediateHandler));
      window.removeEventListener("mousemove", throttledMoveHandler);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [session, signOut, effectiveTimeout, resetActivity, location.pathname]);
}
